/*
  Версия для слабовидящих: кнопка включения и полоса настроек (вариант Б из
  макетов, решение владельца 02.10.2026).

  Кнопка включает сразу самый крупный шрифт чёрным по белому - одно нажатие, и
  страница уже читается. На виду только размер и цвет: полоса из всех настроек
  на телефоне занимала пол-экрана. Интервалы, шрифт, изображения и чтение вслух -
  под «Ещё настройки».

  Только показ и выбор: состояние и его хранение - `useLowVision.js`, вид
  страницы в режиме - `styles.lowvision.css`.
*/

import { useState } from 'react'

const SIZE = [
  { value: '1', label: 'Крупный шрифт', face: 'А' },
  { value: '2', label: 'Очень крупный шрифт', face: 'А' },
  { value: '3', label: 'Самый крупный шрифт', face: 'А' },
]

const THEME = [
  { value: 'bw', label: 'Чёрным по белому', face: 'Ц' },
  { value: 'wb', label: 'Белым по чёрному', face: 'Ц' },
  { value: 'blue', label: 'Тёмно-синим по голубому', face: 'Ц' },
  { value: 'brown', label: 'Коричневым по бежевому', face: 'Ц' },
  { value: 'green', label: 'Зелёным по тёмно-коричневому', face: 'Ц' },
]

const MORE = [
  { name: 'images', title: 'Изображения', options: [['on', 'Показывать'], ['off', 'Не показывать']] },
  { name: 'letters', title: 'Интервал между буквами', options: [['1', 'Обычный'], ['2', 'Средний'], ['3', 'Большой']] },
  // Не «полуторный / двойной»: обычный интервал страницы уже полуторный, и
  // ступень с таким названием не меняла бы ничего.
  { name: 'lines', title: 'Межстрочный интервал', options: [['1', 'Обычный'], ['2', 'Средний'], ['3', 'Большой']] },
  { name: 'font', title: 'Шрифт', options: [['sans', 'Без засечек'], ['serif', 'С засечками']] },
  { name: 'speech', title: 'Чтение вслух', options: [['on', 'Включить'], ['off', 'Выключить']] },
]

function Eye() {
  return (
    <svg className="lv-entry__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

/*
  Группа выбора одного значения. `face` - то, что нарисовано на кнопке
  («А» разного размера, «Ц» в цветах схемы), `label` - что слышит читалка
  экрана; где они совпадают, подпись не дублируется.
*/
function Choice({ title, name, value, options, onChoose }) {
  return (
    <fieldset className="lv-group">
      <legend className="lv-group__title">{title}</legend>
      <div className="lv-group__options">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={`lv-option lv-option--${name}-${option.value}`}
            aria-pressed={option.value === value}
            aria-label={option.face ? option.label : undefined}
            title={option.face ? option.label : undefined}
            onClick={() => onChoose(name, option.value)}
          >
            {option.face ?? option.label}
          </button>
        ))}
      </div>
    </fieldset>
  )
}

export default function LowVisionPanel({ lowVision }) {
  const [more, setMore] = useState(false)
  const { state, canSpeak, turnOn, turnOff, choose } = lowVision

  if (!state.on) {
    return (
      <div className="lv-entry">
        <button type="button" className="button button--quiet lv-entry__button" onClick={turnOn}>
          <Eye />
          Версия для слабовидящих
        </button>
      </div>
    )
  }

  const extra = MORE.filter((item) => item.name !== 'speech' || canSpeak)
  return (
    <section
      className={`lv-panel${more ? ' lv-panel--open' : ''}`}
      aria-label="Настройки версии для слабовидящих"
    >
      <div className="lv-panel__row">
        <Choice title="Размер шрифта" name="size" value={state.size} options={SIZE} onChoose={choose} />
        <Choice title="Цвет сайта" name="theme" value={state.theme} options={THEME} onChoose={choose} />
        <div className="lv-panel__actions">
          <button
            type="button"
            className="lv-option"
            aria-expanded={more}
            onClick={() => setMore(!more)}
          >
            {more ? 'Скрыть настройки' : 'Ещё настройки'}
          </button>
          <button type="button" className="lv-option" onClick={turnOff}>
            Обычная версия
          </button>
        </div>
      </div>
      {more ? (
        <div className="lv-panel__row">
          {extra.map((item) => (
            <Choice
              key={item.name}
              title={item.title}
              name={item.name}
              value={state[item.name]}
              options={item.options.map(([value, label]) => ({ value, label }))}
              onChoose={choose}
            />
          ))}
        </div>
      ) : null}
    </section>
  )
}
